import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'

import type { TaskComment, TaskFile } from '../Contracts/Task.types'

/** Можливі форми відповіді `GET .../comments` (бек віддає `{items}`, SPEC-003 §5.9). */
type CommentsResponse = { items?: TaskComment[]; 'hydra:member'?: TaskComment[] } | TaskComment[] | null

const extractItems = (data: CommentsResponse): TaskComment[] => {
    if (Array.isArray(data)) return data
    const items = data?.items ?? data?.['hydra:member']
    return Array.isArray(items) ? items : []
}

/**
 * Стор стрічки коментарів картки задачі (SPEC-008 §4, наряд 08).
 *
 * Тримає завантажений список коментарів, чернетку нового коментаря (текст +
 * уже завантажені файли-сироти) і стан inline-редагування. Двокроковий upload
 * файлів коментаря — через N13 (`POST /services/tasks/comments/files`), attach —
 * через `fileUuids` у створенні коментаря (N16). Кнопки edit/delete показуються
 * за `_permissions` кожного item (бек рахує автор∨admin — TaskCommentRule), тому
 * стрічку читаємо з `?permissions=true`. Інстанс — per-mount, як TaskHistoryTabStore.
 */
export class TaskCommentsTabStore {
    comments: TaskComment[] = []
    loading = false
    submitting = false

    draftText = ''
    draftFiles: TaskFile[] = []
    uploading = false

    editingUuid: string | null = null
    editingText = ''
    /** Нові файли, завантажені ПІД ЧАС редагування (сироти до збереження). */
    editingFiles: TaskFile[] = []
    /** Окремі лоадери редагування: спільний `submitting` крутив і «Додати
     *  коментар», і «Зберегти» одночасно (репорт замовника). */
    savingEdit = false
    uploadingEdit = false

    private taskUuid: string | undefined

    constructor() {
        makeAutoObservable(this)
    }

    async load(taskUuid: string | undefined): Promise<void> {
        this.taskUuid = taskUuid
        if (!taskUuid) return
        this.loading = true
        try {
            // forceRefresh: обходимо 30с GET-дедуплікацію ядра — стрічка має бути свіжою при відкритті картки.
            const raw = await restClient.get(`/services/tasks/${taskUuid}/comments?permissions=true`, undefined, { forceRefresh: true })
            const items = extractItems(raw as unknown as CommentsResponse)
            runInAction(() => {
                this.comments = items
            })
        } catch {
            runInAction(() => {
                this.comments = []
            })
        } finally {
            runInAction(() => {
                this.loading = false
            })
        }
    }

    setDraftText(value: string): void {
        this.draftText = value
    }

    setEditingText(value: string): void {
        this.editingText = value
    }

    /** Крок 1: завантажити файл коментаря (N13) — створює неприкріплену сироту. */
    async uploadDraftFile(file: File): Promise<void> {
        this.uploading = true
        try {
            const body = new FormData()
            body.append('file', file, file.name)
            const raw = await restClient.post(
                '/services/tasks/comments/files',
                { body },
                { isNotHeaderAddContentType: true }
            )
            const uploaded = raw as unknown as TaskFile
            if (uploaded?.uuid) {
                runInAction(() => {
                    this.draftFiles.push(uploaded)
                })
            }
        } finally {
            runInAction(() => {
                this.uploading = false
            })
        }
    }

    /** Прибрати файл із чернетки — і одразу видалити сироту на сервері (N14). */
    async removeDraftFile(uuid: string): Promise<void> {
        runInAction(() => {
            this.draftFiles = this.draftFiles.filter((f) => f.uuid !== uuid)
        })
        try {
            await restClient.delete(`/services/tasks/comments/files/${uuid}`)
        } catch {
            // Сирота і так прибереться cleanup-командою за 24 год — помилку глушимо.
        }
    }

    /** Створити коментар (N16): текст + прикріплені файли. */
    async submit(): Promise<boolean> {
        const taskUuid = this.taskUuid
        if (!taskUuid) return false
        const text = this.draftText.trim()
        if (text.length === 0) return false

        this.submitting = true
        try {
            const raw = await restClient.post(`/services/tasks/${taskUuid}/comments?permissions=true`, {
                json: { text, fileUuids: this.draftFiles.map((f) => f.uuid).filter(Boolean) },
            })
            const created = raw as unknown as TaskComment
            runInAction(() => {
                // Стрічка — createdAt DESC (найновіші зверху), тож додаємо на початок.
                this.comments.unshift(created)
                this.draftText = ''
                this.draftFiles = []
            })
            return true
        } finally {
            runInAction(() => {
                this.submitting = false
            })
        }
    }

    startEdit(comment: TaskComment): void {
        this.editingUuid = comment.uuid
        this.editingText = comment.text ?? ''
        this.editingFiles = []
    }

    cancelEdit(): void {
        // Сироти, завантажені під час скасованого редагування, прибираємо одразу
        // (cleanup-команда і так знесла б їх за 24 год — але не смітимо)
        for (const file of this.editingFiles) {
            void restClient.delete(`/services/tasks/comments/files/${file.uuid}`)?.catch(() => undefined)
        }
        this.editingUuid = null
        this.editingText = ''
        this.editingFiles = []
    }

    /** Завантажити файл під час редагування (N13) — прикріпиться на «Зберегти». */
    async uploadEditFile(file: File): Promise<void> {
        this.uploadingEdit = true
        try {
            const body = new FormData()
            body.append('file', file, file.name)
            const raw = await restClient.post(
                '/services/tasks/comments/files',
                { body },
                { isNotHeaderAddContentType: true }
            )
            const uploaded = raw as unknown as TaskFile
            if (uploaded?.uuid) {
                runInAction(() => {
                    this.editingFiles.push(uploaded)
                })
            }
        } finally {
            runInAction(() => {
                this.uploadingEdit = false
            })
        }
    }

    /** Прибрати ще не збережений файл редагування — сироту видаляємо одразу. */
    async removeEditFile(uuid: string): Promise<void> {
        runInAction(() => {
            this.editingFiles = this.editingFiles.filter((f) => f.uuid !== uuid)
        })
        try {
            await restClient.delete(`/services/tasks/comments/files/${uuid}`)
        } catch {
            // Сирота прибереться cleanup-командою — помилку глушимо.
        }
    }

    /** Зберегти правку коментаря (N17): текст + додані під час редагування файли. */
    async saveEdit(): Promise<boolean> {
        const uuid = this.editingUuid
        if (!uuid) return false
        const text = this.editingText.trim()
        if (text.length === 0) return false

        this.savingEdit = true
        try {
            const raw = await restClient.patch(`/services/tasks/comments/${uuid}?permissions=true`, {
                json: { text, fileUuids: this.editingFiles.map((f) => f.uuid).filter(Boolean) },
            })
            const updated = raw as unknown as TaskComment
            runInAction(() => {
                this.comments = this.comments.map((c) => (c.uuid === uuid ? updated : c))
                this.editingUuid = null
                this.editingText = ''
                this.editingFiles = []
            })
            return true
        } finally {
            runInAction(() => {
                this.savingEdit = false
            })
        }
    }

    /** Видалити коментар (N18) — бек знесе його файли з БД і диска. */
    async remove(uuid: string): Promise<void> {
        await restClient.delete(`/services/tasks/comments/${uuid}`)
        runInAction(() => {
            this.comments = this.comments.filter((c) => c.uuid !== uuid)
        })
    }

    /** Видалити окремий файл наявного коментаря (N14) — card/feed delete (хвіст 08.1). */
    async deleteCommentFile(commentUuid: string, fileUuid: string): Promise<void> {
        await restClient.delete(`/services/tasks/comments/files/${fileUuid}`)
        runInAction(() => {
            this.comments = this.comments.map((c) =>
                c.uuid === commentUuid ? { ...c, files: (c.files ?? []).filter((f) => f.uuid !== fileUuid) } : c
            )
        })
    }
}
